import { AfterViewInit, Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, ParamMap, Params, Router } from '@angular/router';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Observable, Subject, Subscription } from 'rxjs';
import { fadeInUp400ms } from '../../../../../@vex/animations/fade-in-up.animation';
import { stagger40ms } from '../../../../../@vex/animations/stagger.animation';
import {
  MAT_LEGACY_FORM_FIELD_DEFAULT_OPTIONS as MAT_FORM_FIELD_DEFAULT_OPTIONS,
  MatLegacyFormFieldDefaultOptions as MatFormFieldDefaultOptions,
} from '@angular/material/legacy-form-field';
import icDone from '@iconify/icons-ic/twotone-done';
import icSearch from '@iconify/icons-ic/twotone-search';
import icFilterList from '@iconify/icons-ic/twotone-filter-list';
import icAdd from '@iconify/icons-ic/twotone-add';
import icClose from '@iconify/icons-ic/twotone-close';
import icMoreVert from '@iconify/icons-ic/twotone-more-vert';
import icDelete from '@iconify/icons-ic/twotone-delete';
import icEdit from '@iconify/icons-ic/twotone-edit';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { Sort } from '@angular/material/sort';
import { BaseService } from '../../base.service';
import { FeiColumn } from '../../interfaces/fei-column.interface';
import { ENUM_PERMISSIONS } from '../../../enums/permission.enum';
import { DEFAULT_PAGE_SIZE } from '../../../config/base-table.config';
import { SearchInputDataHeaderStore } from '../../store/search-input-data-header-store';
import { MatSidenav } from '@angular/material/sidenav';
import { debounceTime, switchMap } from 'rxjs/operators';
import { ObjectApiInterface } from '../../interfaces/object-api.interface';
import { FilterSchema, toUrlFilterType, UrlFilterType } from '../../interfaces/filter-schema.interface';
import { SnackbarService } from '../../../services/snackbar.service';
import {
  ListPagingDefaults,
  ListPagingState,
  readListPagingParams,
  writeListPagingParams,
} from '../../../utils/list-query-params.utils';

const RESERVED_QUERY_PARAMS = new Set(['page', 'pageSize', 'sort', 'search', 'archived', 'expired']);
const RANGE_COMPARISONS = new Set(['between', 'fixed', 'greaterThan', 'lessThan']);

@Component({
  template: '',
  animations: [fadeInUp400ms, stagger40ms],
  providers: [
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        appearance: 'standard',
      } as MatFormFieldDefaultOptions,
    },
  ],
})
export class DataTableBase<T> implements OnInit, OnDestroy, AfterViewInit {
  ENUM_PERMISSION = ENUM_PERMISSIONS;
  icFilterList = icFilterList;
  icAdd = icAdd;
  icClose = icClose;
  icDone = icDone;
  icMoreVert = icMoreVert;
  icDelete = icDelete;
  icEdit = icEdit;
  icSearch = icSearch;
  searchCtrl = new UntypedFormControl();
  loading = false;
  dataSource = new MatTableDataSource<T>();
  canUpdateDynamically?: (element: any) => boolean;
  canDeleteDynamically?: (element: any) => boolean;
  sortField: string;
  sortOrder: string;
  requiredFields: string[] = ['*'];
  pageSize = DEFAULT_PAGE_SIZE;
  count = 0;
  displayedColumns = ['actions'];
  feisColumns: Array<FeiColumn>;
  routerLinkDetailRow = ':id';
  selectedItem: T;
  isArchive = false;
  isExpired = false;
  service: BaseService<T>;
  isLoading = false;
  filters: T | any = {};
  /** @deprecated Mécanisme popup legacy — préférer le sidenav (`applyAdvancedFilter` + `filterSchema`). */
  popupForm: UntypedFormGroup = new UntypedFormGroup({});
  countPopupFilter = 0;
  activeFiltersCount = 0;
  protected subscription = new Subscription();
  protected searchStore: SearchInputDataHeaderStore = SearchInputDataHeaderStore.getInstance();
  // Noms distincts de "router"/"route" pour ne pas entrer en conflit avec les mêmes
  // champs déjà déclarés (private) dans de nombreux composants enfants (PRED-3054).
  private readonly filterUrlRouter = inject(Router);
  private readonly filterUrlRoute = inject(ActivatedRoute);
  // Même raison : les enfants déclarent souvent "snackbar" ou "snackbarService".
  private readonly synchronizationSnackbar = inject(SnackbarService);
  /**
   * Seul le composant rendu par la route possède l'URL. Un tableau embarqué (onglet d'une page de détail,
   * modale) reçoit l'ActivatedRoute de son hôte : il ne doit ni lire ni écrire les query params, sinon deux
   * onglets d'une même page se partageraient `page`, `sort` ou `search` (PRED-3054/3175).
   */
  private readonly ownsUrlState = this.filterUrlRoute.snapshot.routeConfig?.component === this.constructor;
  private pagingDefaults: ListPagingDefaults = {};
  /**
   * `false` : seule la pagination (page, taille, tri) est persistée dans l'URL, ni recherche, ni archivés, ni filtres.
   * Pour les listings dont la persistance des filtres n'a pas été faite (PRED-3175 = pagination uniquement).
   */
  protected persistFiltersInUrl = true;
  /**
   * Schéma des filtres avancés du listing : clé API → type/contrôle (PRED-3054).
   * Seules ces clés sont persistées dans l'URL (ids/primitives, jamais les flags internes) ;
   * dérive `urlFilters` et, côté sidenav, la réduction et la restauration des chips.
   */
  protected filterSchema: FilterSchema = {};
  private get urlFilters(): Record<string, UrlFilterType> {
    return Object.fromEntries(Object.entries(this.filterSchema).map(([key, spec]) => [key, toUrlFilterType(spec)]));
  }
  private get pagingState(): ListPagingState {
    return { pageIndex: this.pageIndex, pageSize: this.pageSize, sortField: this.sortField, sortOrder: this.sortOrder };
  }
  isFilterOpen = false;
  @ViewChild('sidenav') sideNav: MatSidenav;
  public pageChangeSubject = new Subject<{ pageIndex: number; pageSize: number }>();
  private searchSubject = new Subject<string>();

  private searchSubscription: any;
  pageIndex = 1;
  search = '';
  constructor() {}

  ngAfterViewInit() {
    // Le header lit la recherche via le store, dont il ne reçoit les émissions qu'une fois sa vue créée
    if (this.search) {
      this.searchStore.updateSearch(this.search);
    }
  }

  /** @final — ne pas overrider : utiliser setupTable()/buildFilters()/fetch()/afterFetch(). */
  ngOnInit() {
    this.hydrateFromQueryParams();
    this.setupTable();

    // Gestion du debounce pour la pagination
    this.subscription.add(
      this.pageChangeSubject
        .pipe(
          debounceTime(300),
          switchMap(({ pageIndex, pageSize }) => {
            this.pageIndex = pageIndex;
            this.pageSize = pageSize;
            return this.fetch(this.buildFilters());
          }),
        )
        .subscribe({
          next: res => {
            const transformedData = this.transformData(res.data);
            this.dataSource = new MatTableDataSource(transformedData);
            this.count = res.totalData;
            this.loading = false;
            this.afterFetch(res);
          },
          error: () => {
            this.loading = false;
          },
        }),
    );

    // Gestion du debounce pour la recherche (évite les requêtes multiples lors de la saisie rapide)
    this.subscription.add(
      this.searchSubject.pipe(debounceTime(400)).subscribe(searchValue => {
        this.search = searchValue;
        this.pageIndex = 1;
        this.initData();
      }),
    );

    this.initData();
  }

  // Dans DataTableBase
  protected transformData(data: any[]): any[] {
    return data; // Implémentation par défaut
  }

  /** @final — ne pas overrider : utiliser setupTable()/buildFilters()/fetch()/afterFetch(). */
  initData(): void {
    this.loading = true;
    this.pageChangeSubject.next({ pageIndex: this.pageIndex, pageSize: this.pageSize });
    this.syncQueryParams();
  }

  /** Une fois, après hydrateFromQueryParams(), avant le 1er fetch : colonnes, entityName, souscriptions hors pipeline. */
  protected setupTable(): void {}

  /** Pur, appelé à chaque fetch. */
  protected buildFilters(): Record<string, unknown> {
    return { ...this.filters, requiredFields: this.requiredFields };
  }

  protected fetch(filters: Record<string, unknown>): Observable<ObjectApiInterface<T[]>> {
    return this.service.findAll(this.pageIndex, this.pageSize, this.sortField, this.sortOrder, this.search, filters);
  }

  /** Effets de bord après dataSource/count (fire-and-forget). */
  protected afterFetch(res: ObjectApiInterface<T[]>): void {}

  /**
   * Restaure l'état du listing depuis l'URL (PRED-3054). Appelé une fois au ngOnInit, avant le premier fetch.
   * Toute valeur absente ou invalide laisse la valeur par défaut en place.
   */
  protected hydrateFromQueryParams(): void {
    this.pagingDefaults = { pageSize: this.pageSize, sortField: this.sortField, sortOrder: this.sortOrder };
    if (!this.ownsUrlState) {
      return;
    }
    const params = this.filterUrlRoute.snapshot.queryParamMap;

    Object.assign(this, readListPagingParams(params, this.pagingState));
    if (!this.persistFiltersInUrl) {
      return;
    }

    this.search = params.get('search') ?? '';

    if (params.get('archived') === 'true') {
      this.isArchive = true;
      this.filters = { ...this.filters, isArchive: true };
    } else if (params.get('expired') === 'true') {
      this.isExpired = true;
      this.filters = { ...this.filters, isExpired: true };
    }

    const restored = this.readUrlFilters(params);
    if (Object.keys(restored).length > 0) {
      this.filters = { ...this.filters, ...restored };
      this.activeFiltersCount = this.countActiveFilters();
    }
  }

  /** Nombre de filtres du schéma réellement renseignés (badge du bouton Filtres). `false` compte, vide/[] non. */
  protected countActiveFilters(filters: Record<string, unknown> = this.filters): number {
    return Object.keys(this.filterSchema).filter(key => !this.isEmptyFilterValue(filters?.[key])).length;
  }

  private isEmptyFilterValue(value: unknown): boolean {
    return value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0);
  }

  private readUrlFilters(params: ParamMap): Record<string, unknown> {
    const restored: Record<string, unknown> = {};

    for (const [key, type] of Object.entries(this.urlFilters)) {
      if (RESERVED_QUERY_PARAMS.has(key)) {
        continue;
      }
      const raw = params.get(key);

      switch (type) {
        case 'string[]': {
          const values = params.getAll(key).filter(Boolean);
          if (values.length > 0) {
            restored[key] = values;
          }
          break;
        }
        case 'id[]': {
          // Restauré en ids bruts : tous les filtres back les acceptent, seuls certains acceptent des objets.
          const ids = params.getAll(key).filter(Boolean);
          if (ids.length > 0) {
            restored[key] = ids;
          }
          break;
        }
        case 'range': {
          const range = this.readRange(raw);
          if (range) {
            restored[key] = range;
          }
          break;
        }
        case 'number':
          if (raw !== null && raw !== '' && !Number.isNaN(Number(raw))) {
            restored[key] = Number(raw);
          }
          break;
        case 'boolean':
          if (raw === 'true' || raw === 'false') {
            restored[key] = raw === 'true';
          }
          break;
        default:
          if (raw) {
            restored[key] = raw;
          }
      }
    }

    return restored;
  }

  /**
   * Reflète l'état courant dans l'URL (PRED-3054), à chaque initData().
   * Les valeurs par défaut sont omises pour qu'un même état produise toujours la même URL.
   */
  protected syncQueryParams(): void {
    if (!this.ownsUrlState) {
      return;
    }
    const filterParams: Params = this.persistFiltersInUrl
      ? {
          search: this.search || null,
          archived: this.isArchive ? true : null,
          expired: this.isExpired ? true : null,
          ...this.writeUrlFilters(),
        }
      : {};
    const queryParams: Params = {
      ...writeListPagingParams(this.pagingState, this.pagingDefaults),
      ...filterParams,
      ...this.extraQueryParams(),
    };

    this.filterUrlRouter.navigate([], { relativeTo: this.filterUrlRoute, queryParams, replaceUrl: true }).then();
  }

  /**
   * État d'écran propre au composant, hors filtres API (ex. onglet actif), à persister avec le listing.
   * syncQueryParams() remplace tous les query params : une clé non renvoyée ici disparaît de l'URL.
   */
  protected extraQueryParams(): Params {
    return {};
  }

  private writeUrlFilters(): Params {
    const written: Params = {};

    for (const key of Object.keys(this.urlFilters)) {
      if (RESERVED_QUERY_PARAMS.has(key)) {
        continue;
      }
      const value = this.filters?.[key];
      if (this.isEmptyFilterValue(value)) {
        continue;
      }

      switch (this.urlFilters[key]) {
        case 'id[]':
          written[key] = value.map((x: any) => (x && typeof x === 'object' ? x._id : x)).filter(Boolean);
          break;
        case 'range': {
          const range = this.writeRange(value);
          if (range) {
            written[key] = range;
          }
          break;
        }
        default:
          written[key] = value;
      }
    }

    return written;
  }

  private writeRange(value: { comparison?: string; min?: unknown; max?: unknown }): string | null {
    if (!RANGE_COMPARISONS.has(value?.comparison ?? '')) {
      return null;
    }
    const bound = (v: unknown) => (v == null || v === '' ? '' : this.toIsoOrString(v));
    const min = bound(value.min);
    const max = bound(value.max);
    const isComplete = value.comparison === 'between' ? min && max : max;

    return isComplete ? `${value.comparison},${min},${max}` : null;
  }

  private readRange(raw: string | null): { comparison: string; min?: unknown; max?: unknown } | null {
    const [comparison, min = '', max = ''] = (raw ?? '').split(',');
    if (!RANGE_COMPARISONS.has(comparison)) {
      return null;
    }
    const isComplete = comparison === 'between' ? min && max : max;
    if (!isComplete) {
      return null;
    }
    const bound = (v: string) => (v === '' ? undefined : Number.isNaN(Number(v)) ? v : Number(v));

    return { comparison, min: bound(min), max: bound(max) };
  }

  private toIsoOrString(value: unknown): string {
    const date =
      value instanceof Date ? value : typeof (value as any)?.toDate === 'function' ? (value as any).toDate() : null;
    return date && !Number.isNaN(date.getTime()) ? date.toISOString() : String(value);
  }

  /** Passe par initData() pour que la page et la taille soient aussi reflétées dans l'URL (PRED-3054). */
  pageEvent(event: PageEvent): void {
    this.pageIndex = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.initData();
  }

  sortEvent(event: Sort): void {
    const column = this.feisColumns.find(x => x.column === event.active);
    // Utiliser propertyDisplay si défini, sinon utiliser le nom de la colonne
    this.sortField = column?.propertyDisplay || event.active;
    this.sortOrder = event.direction;
    this.pageIndex = 1; // Reset to first page when sorting
    this.initData();
  }

  getFilteredData(data: any) {}

  ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
    this.subscription.unsubscribe();
  }

  clearTextFilter(): void {
    this.searchStore.clearSearch();
    // Utiliser searchSubject pour éviter les requêtes dupliquées
    this.searchSubject.next('');
  }

  delayInitData(delay: number): void {
    setTimeout(() => {
      this.initData();
    }, delay);
  }

  onSearchChange(event) {
    this.searchSubject.next(event);
  }

  onShowArchived(event) {
    this.isArchive = event;
    if (event) {
      // Si on active "Archivés", on désactive "Expirés"
      this.isExpired = false;
      this.filters = { ...this.filters, isArchive: true, isExpired: false };
    } else {
      this.filters = { ...this.filters, isArchive: false };
    }
    this.pageIndex = 1; // Reset to first page when changing archive filter
    this.initData();
  }

  onShowExpired(event) {
    this.isExpired = event;
    if (event) {
      // Si on active "Expirés", on désactive "Archivés"
      this.isArchive = false;
      this.filters = { ...this.filters, isExpired: true, isArchive: false };
    } else {
      this.filters = { ...this.filters, isExpired: false };
    }
    this.pageIndex = 1; // Reset to first page when changing expired filter
    this.initData();
  }

  onSynchronization(event) {
    this.isLoading = true; // Affichez le loader

    const items = this.service.synchronizeDocuware().subscribe(
      res => {
        if (res.started) {
          this.synchronizationSnackbar.success('Synchronisation Docuware lancée. Elle peut prendre plusieurs minutes.');
        } else {
          this.synchronizationSnackbar.warning('Une synchronisation Docuware est déjà en cours.');
        }
        this.isLoading = false; // Masquez le loader
      },
      err => {
        this.synchronizationSnackbar.danger('Erreur lors du lancement de la synchronisation Docuware.');
        this.isLoading = false; // Masquez le loader
      },
    );

    this.subscription.add(items);
  }

  createItem(): void {
    // create logic
  }

  updateItem(element: T): void {
    // update logic
  }

  deleteItem(element: T): void {
    // delete logic
  }

  archiveItem(element: T): void {
    // archive logic
  }

  duplicateItem(element: T): void {
    // duplicate logic
  }

  /**
   * @deprecated Mécanisme popup legacy — préférer le sidenav (`applyAdvancedFilter` + `filterSchema`).
   * Ne retire que les clés du `popupForm` : les autres filtres (archivés, radios, dérivés) sont conservés.
   */
  clearPopupFilter(): void {
    this.popupForm.reset();
    const popupKeys = Object.keys(this.popupForm.controls);
    const hadPopupFilters = popupKeys.some(key => !this.isEmptyFilterValue(this.filters?.[key]));
    if (!hadPopupFilters && this.countPopupFilter === 0) {
      return;
    }
    this.filters = Object.fromEntries(Object.entries(this.filters).filter(([key]) => !popupKeys.includes(key)));
    this.countPopupFilter = 0;
    this.pageIndex = 1;
    this.initData();
  }

  beforeValidatePopupFilter(): void {
    // remap filters objet before send request
  }

  /** @deprecated Mécanisme popup legacy — préférer le sidenav (`applyAdvancedFilter` + `filterSchema`). */
  validatePopupFilter(): void {
    this.filters = { ...this.popupForm.value, isArchive: this.isArchive };
    this.beforeValidatePopupFilter();
    this.countPopupFilter = Object.entries(this.popupForm.value).filter((entrie: any) => {
      if (entrie[1] && typeof entrie[1] === 'object' && entrie[1].start !== null) {
        return entrie[1];
      }
      if (entrie[1] && typeof entrie[1] !== 'object') {
        return entrie[1];
      }
    }).length;
    this.pageIndex = 1; // Reset to first page when applying filters
    this.initData();
  }

  applyAdvancedFilter(filters: any) {
    this.activeFiltersCount = this.countActiveFilters(filters);
    // Les cases Archivés/Expirés du header ne font pas partie du sidenav : on les garde alignées avec l'UI.
    // Décochées, rien n'est émis (comportement inchangé : le back applique son défaut).
    const headerToggles = { ...(this.isArchive && { isArchive: true }), ...(this.isExpired && { isExpired: true }) };
    // Préserver les filtres spéciaux (radio buttons, etc.) qui ne font pas partie des filtres avancés
    const preservedFilters = this.getPreservedFilters();
    this.filters = { ...filters, ...headerToggles, ...preservedFilters };
    this.pageIndex = 1; // Reset to first page when applying advanced filters
    this.initData();
  }

  /**
   * Méthode overridable pour préserver certains filtres lors de l'application des filtres avancés
   * Par défaut, ne préserve aucun filtre. Les composants enfants peuvent override cette méthode.
   */
  protected getPreservedFilters(): any {
    return {};
  }

  toggleSidenav() {
    this.sideNav.toggle();
    this.isFilterOpen = !this.sideNav.opened;
  }
  // selectItem(element: T) {
  //     this.selectedItem = element;
  // }
}
